import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of, from } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Environment } from '../../environments/environment.interface';
import { Log, IShortLogger } from './logger.service';

/**
 * Runtime configuration loaded from config.json.
 * Can contain any subset of Environment properties to override at runtime.
 */
export type RuntimeConfig = Partial<Environment>;

/**
 * Enhanced Configuration Service using Angular environment + runtime config pattern.
 *
 * This service implements a hybrid approach:
 * - Development: Uses environment.ts values directly
 * - Production/QA: Loads config.json at runtime (generated by Docker)
 * - Azure SWA: Uses environment values or config.json if available
 *
 * This pattern works perfectly with:
 * - Local development (no HTTP call needed)
 * - Docker deployments (runtime config injection)
 * - Azure Static Web Apps (build-time or runtime config)
 */
@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private readonly logger: IShortLogger<ConfigService> = Log.create(ConfigService);
  private runtimeConfig: RuntimeConfig | null = null;
  private configLoaded = new BehaviorSubject<boolean>(false);

  public readonly configLoaded$ = this.configLoaded.asObservable();

  constructor() {
    if (environment.enableDebugLogging) {
      this.logger.d('ConfigService initialized for environment:', environment.name);
    }
  }

  /**
   * Get the current hostname for environment detection.
   * Protected for testing purposes (can be spied on).
   */
  protected getHostname(): string {
    return window.location.hostname;
  }

  /**
   * Initialize configuration. Should be called during app initialization.
   * For local development, this resolves immediately.
   * For containerized deployments (Docker/ACI), this loads config.json via HTTP.
   *
   * Logic:
   * - Local dev (npm start): Uses environment.ts directly (fast, no HTTP call)
   * - Docker/Cloud (any env): Loads config.json and mutates environment object
   */
  async initializeConfig(): Promise<RuntimeConfig | null> {
    // Local Development: Use environment values directly, no HTTP call needed
    // We detect local dev by checking if we're NOT in production mode AND the hostname is localhost
    const hostname = this.getHostname();
    const isLocalDev = !environment.production &&
      (hostname === 'localhost' || hostname === '127.0.0.1');

    if (isLocalDev && environment.apiUrl && environment.wsUrl) {
      this.runtimeConfig = {
        apiUrl: environment.apiUrl,
        wsUrl: environment.wsUrl
      };
      this.configLoaded.next(true);

      if (environment.enableDebugLogging) {
        this.logger.d('Using local development environment config (no config.json fetch):', this.runtimeConfig);
      }

      return this.runtimeConfig;
    }

    // Cloud/Docker Deployment: Load runtime config from config.json
    // This applies to: Docker Compose, QA ACI, Production ACI
    if (environment.enableDebugLogging) {
      this.logger.d('Detected cloud/containerized deployment, loading config.json...');
    }

    try {
      const config = await this.loadRuntimeConfig();
      if (config) {
        this.runtimeConfig = config;

        // Update environment object for backward compatibility
        // Object.assign automatically merges all properties from config into environment
        // This means ANY property in config.json will override environment.ts
        Object.assign(environment, config);

        this.configLoaded.next(true);

        if (environment.enableDebugLogging) {
          this.logger.d('Loaded runtime config:', this.runtimeConfig);
          this.logger.d('Updated environment object:', { apiUrl: environment.apiUrl, wsUrl: environment.wsUrl });
        }
      }
    } catch (error) {
      this.logger.e('Failed to load runtime config, using fallback values:', error);

      // Fallback to environment defaults if config.json fails
      this.runtimeConfig = {
        apiUrl: environment.apiUrl || 'http://localhost:5001/api',
        wsUrl: environment.wsUrl || 'ws://localhost:5002'
      };

      // Update environment object with fallback values
      Object.assign(environment, this.runtimeConfig);
      this.configLoaded.next(true);
    }
    return this.runtimeConfig;
  }

  /**
   * Load runtime configuration from config.json.
   * This file is generated by Docker at container startup.
   * Uses native fetch() instead of HttpClient to avoid circular dependency.
   */
  private async loadRuntimeConfig(): Promise<RuntimeConfig | Error> {
    try {
      const response = await fetch('/assets/config/config.json');
      const config: RuntimeConfig = await response.json();
      this.logger.d("Loaded config: ", config);
      return config;
    }
    catch (e: any) {
      this.logger.e("Failed to load config:", e.message);
      throw e;
    }
  }

  /**
   * Get the current configuration.
   * Returns environment config for development, runtime config for production.
   */
  public getConfig(): RuntimeConfig {
    // Return cached runtime config if available
    if (this.runtimeConfig) {
      return this.runtimeConfig;
    }

    // Fallback to environment values
    return {
      apiUrl: environment.apiUrl || 'http://localhost:5001/api',
      wsUrl: environment.wsUrl || 'ws://localhost:5002'
    };
  }

  /**
   * Get the API base URL.
   */
  public getApiUrl(): string {
    return this.getConfig().apiUrl || environment.apiUrl;
  }

  /**
   * Get the WebSocket URL.
   */
  public getWsUrl(): string {
    return this.getConfig().wsUrl || environment.wsUrl;
  }

  /**
   * Get the base backend URL (API URL without /api suffix).
   * Used for SignalR hubs, health checks, and other endpoints on the same server.
   */
  public getBaseUrl(): string {
    const apiUrl = this.getApiUrl();
    return apiUrl.replace(/\/api$/, '');
  }

  /**
   * Get environment-specific settings.
   */
  public getEnvironment() {
    return environment;
  }

  /**
   * Check if configuration has been loaded.
   */
  public isConfigLoaded(): boolean {
    return this.configLoaded.value;
  }
}
